// Remboursements (H4-10) : liste filtrée et fiche ; validation par le financier, ordonnancement par
// le maire ou président ou son délégataire (plafond de la délégation contrôlé par le serveur), par
// une autre personne que le validateur ; rejet motivé. L'ordonnancement crée le mouvement négatif ;
// la quittance d'origine reste inchangée.
import { Bandeau, EtatVide, Filtres, Lien, LienBouton, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Undo2 } from 'lucide-react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { LIBELLES_ETAT_REMBOURSEMENT, francs, libelle, tonRemboursement, type Remboursement } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes, type DroitsRecettes } from './NavigationFinances';
import { useRemboursement, useRemboursements } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

function puce(r: Pick<Remboursement, 'etat'>) {
  return <PuceEtat ton={tonRemboursement(r.etat)} libelle={libelle(LIBELLES_ETAT_REMBOURSEMENT, r.etat)} />;
}

function Actions({ r, droits }: { r: Remboursement; droits: DroitsRecettes }) {
  const validateur = r.valide_par !== null && r.valide_par === droits.moi;
  return (
    <div className={styles.actions}>
      {r.etat === 'propose' && droits.modifier ? <FormulaireAction libelle="Valider" titre={`Valider le remboursement ${r.numero}`} explication={`${francs(r.montant)} sur le paiement ${r.reference_paiement ?? ''}. La validation du financier précède l'ordonnancement par le maire ou président.`} chemin={`/recettes/remboursements/${r.id}/valider`} succes="Remboursement validé." /> : null}
      {r.etat === 'valide' && droits.decider ? (
        <FormulaireAction
          libelle="Ordonner"
          titre={`Ordonner le remboursement ${r.numero}`}
          explication={validateur ? 'Vous avez validé ce remboursement : la règle des quatre yeux réserve son ordonnancement à une autre personne (le serveur refusera).' : `${francs(r.montant)} : le mouvement négatif est passé sur le paiement et ses créances ; le redevable est notifié. Un délégataire est limité au plafond de sa délégation.`}
          chemin={`/recettes/remboursements/${r.id}/ordonner`}
          succes="Remboursement ordonné."
        />
      ) : null}
      {(r.etat === 'propose' || r.etat === 'valide') && (droits.modifier || droits.decider) ? <FormulaireAction libelle="Rejeter" titre={`Rejeter le remboursement ${r.numero}`} explication="Le rejet est motivé et journalisé ; le paiement reste confirmé." chemin={`/recettes/remboursements/${r.id}/rejeter`} champs={[{ nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true }]} succes="Remboursement rejeté." variante="danger" /> : null}
    </div>
  );
}

export function PageRemboursement() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const fiche = useRemboursement(id);
  const r = fiche.data;
  const detail = r?.detail as { non_impute?: number; imputations?: { creance_id?: string; montant?: number }[] } | null | undefined;
  return (
    <>
      <EnTetePage etiquette="Finances, remboursement" titre={r ? `Remboursement ${r.numero}` : 'Remboursement'} description={r ? <span className={commun.ligne}>{puce(r)}</span> : null} actions={<LienBouton href="/recettes/remboursements" icone={ArrowLeft}>Remboursements</LienBouton>} />
      <NavigationFinances />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L2-A" fonction="La fiche du remboursement">
        {r ? (
          <div className={commun.deuxColonnes}>
            <Panneau titre="Remboursement">
              <dl className={commun.definitions}>
                <dt>Montant</dt>
                <dd className={`${styles.mono} ${styles.montantFort}`}>{francs(r.montant)}</dd>
                <dt>Paiement</dt>
                <dd><Lien href={`/recettes/paiements/${r.paiement_id}`} className={styles.mono}>{r.reference_paiement ?? r.paiement_id}</Lien></dd>
                <dt>Motif</dt>
                <dd>{r.motif}</dd>
                <dt>Proposé le</dt>
                <dd>{dateHeure(r.propose_le)}</dd>
                <dt>Validé le</dt>
                <dd>{r.valide_le ? dateHeure(r.valide_le) : 'non validé'}</dd>
                <dt>Ordonné le</dt>
                <dd>{r.ordonne_le ? `${dateHeure(r.ordonne_le)}${r.delegation_id ? ' (par délégation)' : ''}` : 'non ordonné'}</dd>
                {r.motif_rejet ? (
                  <>
                    <dt>Rejet</dt>
                    <dd>{r.motif_rejet}</dd>
                  </>
                ) : null}
                {r.piece_document_id ? (
                  <>
                    <dt>Pièce</dt>
                    <dd>jointe</dd>
                  </>
                ) : null}
              </dl>
              {r.etat === 'ordonne' && detail ? (
                <p className={styles.sous}>
                  Mouvement : {typeof detail.non_impute === 'number' && detail.non_impute > 0 ? `${francs(detail.non_impute)} sur la part non imputée ; ` : ''}
                  {(detail.imputations ?? []).map((i) => `${francs(-(i.montant ?? 0))} sur la créance ${(i.creance_id ?? '').slice(0, 8)}`).join(', ') || 'aucune imputation négative'}.
                </p>
              ) : null}
            </Panneau>
            <Panneau titre="Actions">
              {!droits.modifier && !droits.decider ? <p className={commun.na}>Validation par le financier, ordonnancement par le maire ou président.</p> : null}
              <Actions r={r} droits={droits} />
              {r.etat === 'ordonne' ? <Bandeau type="succes">Remboursement ordonné ; la quittance d&apos;origine reste valide.</Bandeau> : null}
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}

export function PageRemboursements() {
  const droits = useDroitsRecettes();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-remboursements', { etat: '' });
  const liste = useRemboursements({ etat: filtres.etat ?? '' });
  const colonnes: ColonneTableau<Remboursement>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (r) => r.numero, cellule: (r) => <Lien href={`/recettes/remboursements/${r.id}`} className={commun.reference}>{r.numero}</Lien> },
    { id: 'paiement', entete: 'Paiement', cellule: (r) => <Lien href={`/recettes/paiements/${r.paiement_id}`} className={styles.mono}>{r.reference_paiement ?? r.paiement_id.slice(0, 8)}</Lien> },
    { id: 'montant', entete: 'Montant', numerique: true, valeurTri: (r) => r.montant, cellule: (r) => <span className={styles.montant}>{francs(r.montant)}</span> },
    { id: 'motif', entete: 'Motif', cellule: (r) => r.motif },
    { id: 'propose', entete: 'Proposé le', valeurTri: (r) => r.propose_le ?? null, cellule: (r) => dateHeure(r.propose_le) },
    { id: 'etat', entete: 'État', valeurTri: (r) => r.etat, cellule: puce },
    { id: 'actions', entete: 'Actions', masquable: false, cellule: (r) => <Actions r={r} droits={droits} /> },
  ];
  return (
    <>
      <EnTetePage etiquette="Finances" titre="Remboursements" description="Proposés depuis la fiche d'un paiement confirmé, validés par le financier, ordonnés par le maire ou président ou son délégataire (jamais la personne qui a validé)." />
      <NavigationFinances />
      <Filtres libelle="Filtres des remboursements" groupes={[{ id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...Object.entries(LIBELLES_ETAT_REMBOURSEMENT).map(([valeur, l]) => ({ valeur, libelle: l }))] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des remboursements">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun remboursement" icone={Undo2}>
            <p>Aucun remboursement ne correspond à ce filtre.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Remboursements" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(r) => r.id} />
        )}
      </EtatRequete>
    </>
  );
}
